/**
 * Creamy Loots — All Products Page JS
 * Swatches → image swap · Size selection · Qty · ATC · Reveal · Sort
 */
(function () {
  'use strict';

  /* ── HELPERS ────────────────────────────────────────────────────────────── */
  const qs  = (sel, ctx) => (ctx || document).querySelector(sel);
  const qsa = (sel, ctx) => [...(ctx || document).querySelectorAll(sel)];
  const fmt = n => '$' + parseInt((n / 100).toFixed(0)).toLocaleString('es-CO');

  /* ── STAGGER REVEAL ─────────────────────────────────────────────────────── */

  function initReveal() {
    const cards = qsa('.cl-ap__card');
    if (!cards.length) return;

    const obs = new IntersectionObserver(
      entries => {
        entries.forEach(e => {
          if (e.isIntersecting) {
            const delay = parseInt(e.target.dataset.revealDelay || 0);
            setTimeout(() => e.target.classList.add('is-revealed'), delay);
            obs.unobserve(e.target);
          }
        });
      },
      { threshold: 0.06, rootMargin: '0px 0px -24px 0px' }
    );

    // Assign stagger delays in batches of columns
    const cols = window.innerWidth >= 1280 ? 4 : window.innerWidth >= 768 ? 3 : 2;
    cards.forEach((card, i) => {
      card.dataset.revealDelay = (i % cols) * 90;
      obs.observe(card);
    });
  }

  /* ── VARIANT LOOKUP ─────────────────────────────────────────────────────── */

  function getVariants(card) {
    const el = qs('[data-ap-variants]', card);
    if (!el) return [];
    try { return JSON.parse(el.textContent); } catch { return []; }
  }

  function findVariant(variants, colorOpt, colorPos, sizeOpt, sizePos) {
    return variants.find(v => {
      const colorMatch = !colorOpt || (v[`option${colorPos}`] === colorOpt);
      const sizeMatch  = !sizeOpt  || (v[`option${sizePos}`]  === sizeOpt);
      return colorMatch && sizeMatch;
    }) || null;
  }

  function getSelected(card) {
    return {
      color:    card.dataset.apColor    || null,
      size:     card.dataset.apSize     || null,
      colorPos: card.dataset.apColorPos || '2',
      sizePos:  card.dataset.apSizePos  || '1',
    };
  }

  /* ── UPDATE CARD UI ─────────────────────────────────────────────────────── */

  function updateCard(card, variant) {
    if (!variant) return;

    // Update price
    const priceEl   = qs('.cl-ap__price', card);
    const compareEl = qs('.cl-ap__price-compare', card);
    const discEl    = qs('.cl-ap__price-discount', card);

    if (priceEl) {
      if (variant.compare_at_price > variant.price) {
        priceEl.textContent = fmt(variant.price);
        priceEl.classList.add('cl-ap__price--sale');
        if (compareEl) { compareEl.textContent = fmt(variant.compare_at_price); compareEl.hidden = false; }
        if (discEl) {
          const pct = Math.round((variant.compare_at_price - variant.price) * 100 / variant.compare_at_price);
          discEl.textContent = `-${pct}%`;
          discEl.hidden = false;
        }
      } else {
        priceEl.textContent = fmt(variant.price);
        priceEl.classList.remove('cl-ap__price--sale');
        if (compareEl) compareEl.hidden = true;
        if (discEl) discEl.hidden = true;
      }
    }

    // Update ATC button
    const atcBtn  = qs('.cl-ap__atc', card);
    const idInput = qs('[name="id"]', card);
    const idleEl  = qs('.cl-ap__atc-idle', card);

    if (atcBtn && idInput) {
      idInput.value = variant.id;
      // Actualiza solo el texto — no borrar el ícono SVG
      const idleTextFull  = qs('.cl-ap__atc-text-full', atcBtn);
      const idleTextShort = qs('.cl-ap__atc-text-short', atcBtn);
      if (variant.available) {
        atcBtn.disabled = false;
        if (idleTextFull)  idleTextFull.textContent  = 'AGREGAR AL CARRITO';
        if (idleTextShort) idleTextShort.textContent = 'AL CARRITO';
      } else {
        atcBtn.disabled = true;
        if (idleTextFull)  idleTextFull.textContent  = 'AGOTADO';
        if (idleTextShort) idleTextShort.textContent = 'AGOTADO';
      }
    }

    // Update variant image if available
    if (variant.featured_image) {
      const img = qs('.cl-ap__card-img', card);
      if (img) swapImage(img, variant.featured_image.src);
    }
  }

  /* ── IMAGE SWAP ─────────────────────────────────────────────────────────── */

  function swapImage(img, src) {
    if (img.src.includes(src.split('?')[0])) return; // same image
    img.classList.add('is-swapping');
    setTimeout(() => {
      img.src = src;
      img.srcset = ''; // clear srcset so new src takes effect
      img.onload = () => img.classList.remove('is-swapping');
      // Fallback if already cached
      if (img.complete) img.classList.remove('is-swapping');
    }, 150);
  }

  /* ── SWATCHES ───────────────────────────────────────────────────────────── */

  function initSwatches(card) {
    const swatches  = qsa('.cl-ap__swatch', card);
    if (!swatches.length) return;

    swatches.forEach(sw => {
      // Hover on desktop: preview image
      sw.addEventListener('mouseenter', () => {
        const src = sw.dataset.imgSrc;
        if (src) {
          const img = qs('.cl-ap__card-img', card);
          if (img) {
            img._prevSrc = img.src;
            img._prevSrcset = img.srcset;
            img.src = src;
            img.srcset = '';
          }
        }
      });

      sw.addEventListener('mouseleave', () => {
        // Only restore if not actively selected
        if (!sw.classList.contains('is-active')) {
          const img = qs('.cl-ap__card-img', card);
          if (img && img._prevSrc && !sw.closest('.cl-ap__swatches').querySelector('.is-active')) {
            img.src = img._prevSrc;
            img.srcset = img._prevSrcset || '';
          }
        }
      });

      // Click: lock color selection + update image + update CTA href
      sw.addEventListener('click', e => {
        e.preventDefault();
        swatches.forEach(s => s.classList.remove('is-active'));
        sw.classList.add('is-active');

        const colorVal = sw.dataset.color;
        card.dataset.apColor = colorVal;

        // Swap main image
        const src = sw.dataset.imgSrc;
        if (src) {
          const img = qs('.cl-ap__card-img', card);
          if (img) { img._prevSrc = src; swapImage(img, src); }
        }

        // Find best variant for selected color (first available size)
        const colorPos = card.dataset.apColorPos || '2';
        const sizePos  = card.dataset.apSizePos  || '1';
        const variants = getVariants(card);
        const variant  = findVariant(variants, colorVal, colorPos, null, sizePos);

        // Update price display
        if (variant) updateCard(card, variant);

        // Update CTA link href with the variant's ID so PDP pre-selects this color
        const ctaLink = qs('.cl-ap__cta-link[data-ap-cta]', card);
        if (ctaLink && variant) {
          const baseUrl = ctaLink.dataset.baseUrl || ctaLink.href.split('?')[0];
          ctaLink.href = `${baseUrl}?variant=${variant.id}`;
        }
      });
    });

    // Auto-activate first swatch (without click to avoid hover side-effects)
    if (swatches[0]) {
      swatches[0].classList.add('is-active');
      card.dataset.apColor = swatches[0].dataset.color;
    }
  }

  /* ── SIZE CHIPS ─────────────────────────────────────────────────────────── */

  function initSizes(card) {
    const chips = qsa('.cl-ap__size-chip', card);
    if (!chips.length) return;

    chips.forEach(chip => {
      if (chip.classList.contains('is-oos') || chip.disabled) return;

      chip.addEventListener('click', () => {
        chips.forEach(c => c.classList.remove('is-active'));
        chip.classList.add('is-active');
        card.dataset.apSize = chip.dataset.size;

        const { colorOpt, colorPos, sizePos } = {
          colorOpt: card.dataset.apColor    || null,
          colorPos: card.dataset.apColorPos || '2',
          sizePos:  card.dataset.apSizePos  || '1',
        };
        const variants = getVariants(card);
        const variant  = findVariant(variants, colorOpt, colorPos, chip.dataset.size, sizePos);
        if (variant) updateCard(card, variant);
      });
    });

    // Auto-select first available size
    const firstAvail = chips.find(c => !c.classList.contains('is-oos') && !c.disabled);
    if (firstAvail) firstAvail.click();
  }

  /* ── QUANTITY ───────────────────────────────────────────────────────────── */

  function initQty(card) {
    const minusBtn = qs('.cl-ap__qty-btn--minus', card);
    const plusBtn  = qs('.cl-ap__qty-btn--plus', card);
    const valEl    = qs('.cl-ap__qty-val', card);
    if (!minusBtn || !plusBtn || !valEl) return;

    const update = () => {
      const v = parseInt(valEl.value);
      minusBtn.disabled = v <= 1;
    };

    minusBtn.addEventListener('click', () => {
      const v = parseInt(valEl.value);
      if (v > 1) { valEl.value = v - 1; update(); }
    });

    plusBtn.addEventListener('click', () => {
      const v = parseInt(valEl.value);
      const max = parseInt(valEl.dataset.max || 99);
      if (v < max) { valEl.value = v + 1; update(); }
    });

    update();
  }

  /* ── ADD TO CART ────────────────────────────────────────────────────────── */

  function initATC(card) {
    const btn     = qs('.cl-ap__atc', card);
    const form    = qs('[data-ap-form]', card);
    if (!btn || !form) return;

    const idle    = qs('.cl-ap__atc-idle', btn);
    const adding  = qs('.cl-ap__atc-adding', btn);
    const done    = qs('.cl-ap__atc-done', btn);
    const errEl   = qs('.cl-ap__atc-error', btn);

    btn.addEventListener('click', async e => {
      e.preventDefault();
      if (btn.disabled || btn.classList.contains('is-adding')) return;

      // Validate: need at least a variant selected
      const idInput = qs('[name="id"]', form);
      const qty     = qs('[name="quantity"]', form);

      if (!idInput?.value || idInput.value === '0') {
        // Flash size selector
        const sizesEl = qs('.cl-ap__sizes', card);
        if (sizesEl) {
          sizesEl.classList.add('is-error');
          setTimeout(() => sizesEl.classList.remove('is-error'), 600);
        }
        return;
      }

      // Loading state
      btn.classList.add('is-adding');
      if (idle) idle.hidden = true;
      if (adding) { adding.hidden = false; adding.removeAttribute('aria-hidden'); }

      try {
        const res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify({
            id: parseInt(idInput.value),
            quantity: parseInt(qty?.value || 1),
          }),
        });

        if (!res.ok) throw new Error('cart_error');

        // Success
        btn.classList.remove('is-adding');
        btn.classList.add('is-done');
        if (adding) adding.hidden = true;
        if (done) { done.hidden = false; done.removeAttribute('aria-hidden'); }

        document.dispatchEvent(new CustomEvent('cart:add', { bubbles: true }));
        updateCartCount();

        setTimeout(() => {
          btn.classList.remove('is-done');
          if (done) { done.hidden = true; done.setAttribute('aria-hidden', 'true'); }
          if (idle) idle.hidden = false;
        }, 2200);

      } catch {
        btn.classList.remove('is-adding');
        btn.classList.add('is-error');
        if (adding) adding.hidden = true;
        if (idle) idle.hidden = false;
        if (errEl) { idle.hidden = true; errEl.hidden = false; errEl.removeAttribute('aria-hidden'); }

        setTimeout(() => {
          btn.classList.remove('is-error');
          if (errEl) { errEl.hidden = true; errEl.setAttribute('aria-hidden', 'true'); }
          if (idle) idle.hidden = false;
        }, 2800);
      }
    });
  }

  /* ── CART COUNT ─────────────────────────────────────────────────────────── */

  /* Pinta el conteo sin romper el markup del header:
     [data-cart-count] es el <a>/<button> completo (SVG + "CART [ ]"). */
  function paintCartCount(count) {
    const label = 'Carrito: ' + count + ' ' + (count === 1 ? 'producto' : 'productos');
    document.querySelectorAll('[data-cart-count]').forEach(el => {
      el.setAttribute('data-cart-count', count);
      el.setAttribute('aria-label', label);
    });
    document.querySelectorAll('.cl-header__cart-count').forEach(el => {
      el.textContent = count;
    });
    document.querySelectorAll('.cl-mobile-menu__cart').forEach(el => {
      el.textContent = 'CARRITO [' + count + ']';
    });
  }

  function updateCartCount() {
    fetch('/cart.js', { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
      .then(r => r.json())
      .then(cart => {
        paintCartCount(cart.item_count);
        document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart }, bubbles: true }));
      })
      .catch(() => {});
  }

  /* ── WISHLIST (local storage) ───────────────────────────────────────────── */

  function initWishlist() {
    const key = 'cl-wishlist';
    const saved = JSON.parse(localStorage.getItem(key) || '[]');

    qsa('.cl-ap__wishlist').forEach(btn => {
      const id = btn.dataset.productId;
      if (saved.includes(id)) btn.classList.add('is-saved');

      btn.addEventListener('click', e => {
        e.preventDefault();
        const list = JSON.parse(localStorage.getItem(key) || '[]');
        if (list.includes(id)) {
          const updated = list.filter(x => x !== id);
          localStorage.setItem(key, JSON.stringify(updated));
          btn.classList.remove('is-saved');
        } else {
          list.push(id);
          localStorage.setItem(key, JSON.stringify(list));
          btn.classList.add('is-saved');
        }
      });
    });
  }

  /* ── FILTER ACTIVE STATE (from URL) ─────────────────────────────────────── */

  function initFilterBar() {
    const params  = new URLSearchParams(window.location.search);
    const activeTag = params.get('filter.p.tag') || params.get('tag') || 'all';
    const sortBy    = params.get('sort_by') || '';

    qsa('.cl-ap__filter-btn').forEach(btn => {
      if (btn.dataset.filterTag === activeTag) btn.classList.add('is-active');
    });

    const sortSel = qs('.cl-ap__sort-select');
    if (sortSel && sortBy) sortSel.value = sortBy;

    // Sort change → redirect
    if (sortSel) {
      sortSel.addEventListener('change', () => {
        const url = new URL(window.location.href);
        url.searchParams.set('sort_by', sortSel.value);
        window.location.href = url.toString();
      });
    }
  }

  /* ── INIT ALL CARDS ─────────────────────────────────────────────────────── */

  function initCards() {
    qsa('[data-ap-card]').forEach(card => {
      initSwatches(card);
      initATC(card); // Solo activo para productos sin variantes (has_only_default_variant)
    });
  }

  /* ── MAIN ───────────────────────────────────────────────────────────────── */

  function init() {
    initReveal();
    initCards();
    initWishlist();
    initFilterBar();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Shopify theme editor: re-init when section reloads
  document.addEventListener('shopify:section:load', function (e) {
    if (e.target.querySelector('[data-ap-card]')) {
      initReveal();
      initCards();
      initWishlist();
      initFilterBar();
    }
  });

})();
